// SPDX-FileCopyrightText: 2023-2026 NukeHub Developers
// SPDX-License-Identifier: BSD-2-Clause

import { createFileRoute } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { useState, useEffect } from 'react'
import {
  HeartPulse,
  Database,
  Cpu,
  HardDrive,
  XCircle,
  RotateCcw,
  Server,
  Mail,
  Container,
  Wifi,
  Search,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Filter,
  AlertTriangle,
  CheckCircle2,
} from 'lucide-react'
import { FloatingHeader } from '../components/layout/floating-header'
import type { StatCardProps } from '../components/data/stat-card'
import { StatusBadge } from '../components/data/status-badge'
import { Select, SelectItem } from '../components/ui/select'
import { useHealthMonitoring } from '../hooks/use-health-monitoring'
import { useWebSocket } from '../hooks/use-websocket'
import { useAuthStore, PERMISSIONS } from '../stores/auth-store'
import { PermissionGuard } from '../components/permission-guard'
import { cn, formatDate, formatBytes } from '../lib/utils'
import { Tooltip } from '../components/ui/tooltip'
import { SkeletonCard, SkeletonStatCard, SkeletonTable } from '../components/feedback/skeleton'
import { springs } from '../lib/animations'
import { useQueryClient } from '@tanstack/react-query'

export const Route = createFileRoute('/admin/health')({
  component: AdminHealthPage,
})

function AdminHealthPage() {
  return (
    <PermissionGuard permission={PERMISSIONS.ADMIN_ACCESS} redirectTo="/admin">
      <AdminHealthContent />
    </PermissionGuard>
  )
}

const STATUS_OPTIONS = [
  { label: 'All', value: '' },
  { label: 'Healthy', value: 'healthy' },
  { label: 'Unhealthy', value: 'unhealthy' },
  { label: 'Unknown', value: 'unknown' },
  { label: 'Restarting', value: 'restarting' },
  { label: 'Restart Failed', value: 'restart_failed' },
]

function AdminHealthContent() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const queryClient = useQueryClient()

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300)
    return () => clearTimeout(timer)
  }, [searchQuery])

  useEffect(() => {
    queueMicrotask(() => setPage(1))
  }, [statusFilter, debouncedSearch])

  const { data, isLoading, isError, refetch } = useHealthMonitoring({
    page,
    limit: 20,
    status: statusFilter || null,
    search: debouncedSearch || null,
  })

  const { isConnected, subscribe, unsubscribe, onMessage } = useWebSocket()
  const isAdmin = useAuthStore((state) => state.hasPermission(PERMISSIONS.ADMIN_ACCESS))

  useEffect(() => {
    if (isConnected && isAdmin) {
      subscribe('global')
      return () => unsubscribe('global')
    }
  }, [isConnected, isAdmin, subscribe, unsubscribe])

  useEffect(() => {
    const cleanup = onMessage((message) => {
      if (message.event === 'health:system') {
        queryClient.invalidateQueries({ queryKey: ['health-monitoring'] })
      }
    })
    return cleanup
  }, [onMessage, queryClient])

  if (isLoading) {
    return (
      <div className="min-h-screen space-y-6">
        <FloatingHeader
          title="Health Monitoring"
          subtitle="System services and container health status"
          icon={HeartPulse}
          backTo="/admin"
        />
        <div className="px-6 lg:px-10 pb-10 space-y-6">
          {/* System Services skeleton */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              System Services
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonCard key={i} rows={1} />
              ))}
            </div>
          </section>
          {/* System Resources skeleton */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              System Resources
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <SkeletonStatCard key={i} />
              ))}
            </div>
          </section>
          {/* Container Health skeleton */}
          <section>
            <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
              Container Health
            </h2>
            <div className="bubble p-6">
              <SkeletonTable rows={5} columns={6} />
            </div>
          </section>
        </div>
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-2">
          <XCircle className="w-10 h-10 text-red-400 mx-auto" />
          <p className="text-muted-foreground">Failed to load health data</p>
          <button onClick={() => refetch()} className="text-sm text-primary hover:underline">
            Retry
          </button>
        </div>
      </div>
    )
  }

  const system = data.system
  const containers = data.containers
  const recentRestarts = data.recent_restarts
  const pagination = containers.pagination
  const resources = system.resources

  const systemHealthy = system.status === 'healthy'
  const failingContainers =
    containers.unhealthy_count + containers.unknown_count + containers.restart_failed_count

  const headerStats: StatCardProps[] = [
    {
      title: 'System Status',
      value: system.status,
      icon: systemHealthy ? CheckCircle2 : AlertTriangle,
      iconColor: systemHealthy ? 'text-emerald-400' : 'text-amber-400',
      bgColor: 'bg-emerald-500/10',
    },
    {
      title: 'Running Servers',
      value: pagination.total,
      icon: Server,
      iconColor: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
    },
    {
      title: 'Unhealthy',
      value: failingContainers,
      icon: AlertTriangle,
      iconColor: failingContainers > 0 ? 'text-red-400' : 'text-emerald-400',
      bgColor: failingContainers > 0 ? 'bg-red-500/10' : 'bg-emerald-500/10',
    },
    {
      title: 'Restarts (24h)',
      value: recentRestarts.length,
      icon: RotateCcw,
      iconColor: 'text-violet-400',
      bgColor: 'bg-violet-500/10',
    },
  ]

  return (
    <div className="min-h-screen space-y-6">
      <FloatingHeader
        title="Health Monitoring"
        subtitle="System services and container health status"
        icon={HeartPulse}
        backTo="/admin"
        stats={headerStats}
      />

      <div className="px-6 lg:px-10 pb-10 space-y-6">
        {/* System Services */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.gentle, delay: 0.05 }}
        >
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            System Services
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <ServiceCard name="Database" health={system.services.database} icon={Database} />
            <ServiceCard name="Redis" health={system.services.redis} icon={Wifi} />
            <ServiceCard
              name={system.services.containers?.runtime || 'Containers'}
              health={system.services.containers}
              icon={Container}
            />
            <ServiceCard name="SMTP" health={system.services.smtp} icon={Mail} />
            <ServiceCard name="Partitions" health={system.services.partitions} icon={HardDrive} />
          </div>
        </motion.section>

        {/* System Resources */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.gentle, delay: 0.1 }}
        >
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
            System Resources
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* CPU Card */}
            <ResourceCard
              label="CPU"
              percent={resources.cpu.percent}
              icon={Cpu}
              details={[
                {
                  label: 'Cores',
                  value: `${resources.cpu.count} (${resources.cpu.count_logical} threads)`,
                },
                ...(resources.cpu.freq_mhz
                  ? [{ label: 'Frequency', value: `${resources.cpu.freq_mhz} MHz` }]
                  : []),
              ]}
            />
            {/* Memory Card */}
            <ResourceCard
              label="Memory"
              percent={resources.memory.percent}
              icon={HardDrive}
              details={[
                { label: 'Total', value: formatBytes(resources.memory.total_bytes) },
                { label: 'Used', value: formatBytes(resources.memory.used_bytes) },
                { label: 'Available', value: formatBytes(resources.memory.available_bytes) },
              ]}
            />
            {/* Root Disk Card */}
            <DiskCard label="Root" disk={resources.disk} icon={HardDrive} />
            {/* Container Disk Card */}
            {resources.container_disk && (
              <DiskCard
                label={shortenPath(resources.container_disk.path)}
                disk={resources.container_disk}
                icon={Container}
              />
            )}
          </div>
          {resources.load_average && (
            <div className="mt-3 text-sm text-muted-foreground">
              Load average:{' '}
              <span className="font-mono tabular-nums">
                {resources.load_average.map((v) => v.toFixed(2)).join(' / ')}
              </span>
            </div>
          )}
        </motion.section>

        {/* Container Health */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ ...springs.gentle, delay: 0.15 }}
          className="bubble p-6 space-y-4"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
                <Server className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-semibold text-base">Container Health</h3>
                <p className="text-xs text-muted-foreground">
                  {pagination.total} running servers monitored
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isConnected && (
                <span className="text-xs text-emerald-400 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  Live
                </span>
              )}
              <Tooltip content="Refresh">
                <button
                  onClick={() => refetch()}
                  className="p-1.5 rounded-lg hover:bg-accent transition-colors text-muted-foreground"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
              </Tooltip>
            </div>
          </div>

          {/* Status summary */}
          <div className="flex flex-wrap gap-2">
            {Object.entries(containers.status_counts).map(([status, count]) => (
              <StatusBadge
                key={status}
                status={mapHealthStatus(status)}
                label={`${status}: ${count}`}
                size="sm"
              />
            ))}
            {Object.keys(containers.status_counts).length === 0 && (
              <span className="text-sm text-muted-foreground">No running containers</span>
            )}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search server or user..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 rounded-lg bg-input/50 border border-input text-sm focus:outline-none focus:ring-2 focus:ring-ring/50"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <Select
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                placeholder="All"
                className="w-36"
              >
                {STATUS_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </Select>
            </div>
          </div>

          {/* Table */}
          {containers.latest_checks.length > 0 ? (
            <>
              <div className="overflow-x-auto rounded-xl border border-border/50">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border/50 bg-card/50">
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                        Server
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                        Status
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                        Failures
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                        Last Check
                      </th>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                        Output
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {containers.latest_checks.map((check) => (
                      <tr
                        key={check.id}
                        className={cn(
                          'border-b border-border/30 hover:bg-card/40 transition-colors',
                          check.consecutive_failures >= 3 && 'bg-red-500/5'
                        )}
                      >
                        <td className="px-4 py-3">
                          <div className="font-medium">{check.server_name}</div>
                          <div className="text-xs text-muted-foreground">{check.username}</div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge status={mapHealthStatus(check.status)} size="sm" />
                        </td>
                        <td className="px-4 py-3">
                          {check.consecutive_failures > 0 ? (
                            <span
                              className={cn(
                                'font-mono font-medium',
                                check.consecutive_failures >= 3 ? 'text-red-400' : 'text-amber-400'
                              )}
                            >
                              {check.consecutive_failures}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {check.checked_at ? formatDate(check.checked_at) : '—'}
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          {check.output ? (
                            <Tooltip content={check.output}>
                              <div className="truncate text-muted-foreground cursor-help">
                                {check.output}
                              </div>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    Showing {(page - 1) * pagination.limit + 1} -{' '}
                    {Math.min(page * pagination.limit, pagination.total)} of {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      disabled={page <= 1}
                      className="p-2 rounded-lg border border-border/50 hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-sm tabular-nums">
                      {page} / {pagination.total_pages}
                    </span>
                    <button
                      onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                      disabled={page >= pagination.total_pages}
                      className="p-2 rounded-lg border border-border/50 hover:bg-accent disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-muted-foreground border border-dashed border-border/50 rounded-xl">
              {debouncedSearch || statusFilter
                ? 'No running containers match your filters'
                : 'No running containers currently being monitored'}
            </div>
          )}
        </motion.section>

        {/* Recent Auto-Restart Events */}
        {recentRestarts.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ ...springs.gentle, delay: 0.2 }}
            className="bubble p-6 space-y-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                <RotateCcw className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <h3 className="font-semibold text-base">Recent Auto-Restart Events</h3>
                <p className="text-xs text-muted-foreground">Last 24 hours</p>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-border/50">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/50 bg-card/50">
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      Server
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      Status
                    </th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">Time</th>
                    <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recentRestarts.map((event) => (
                    <tr
                      key={event.id}
                      className="border-b border-border/30 hover:bg-card/40 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium">{event.server_name}</div>
                        <div className="text-xs text-muted-foreground">{event.username}</div>
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          status={event.status === 'restarting' ? 'pending' : 'error'}
                          label={event.status}
                          size="sm"
                        />
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">
                        {event.checked_at ? formatDate(event.checked_at) : '—'}
                      </td>
                      <td className="px-4 py-3 max-w-xs">
                        {event.output ? (
                          <Tooltip content={event.output}>
                            <div className="truncate text-muted-foreground cursor-help">
                              {event.output}
                            </div>
                          </Tooltip>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.section>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                           */
/* ------------------------------------------------------------------ */

function shortenPath(path?: string): string {
  if (!path) return 'Disk'
  if (path === '/') return 'Root'
  const base = path.split('/').filter(Boolean).pop()
  return base ? base.charAt(0).toUpperCase() + base.slice(1) : 'Disk'
}

function mapHealthStatus(
  status: string
): 'running' | 'stopped' | 'pending' | 'error' | 'warning' | 'info' {
  switch (status) {
    case 'healthy':
      return 'running'
    case 'unhealthy':
      return 'error'
    case 'restarting':
      return 'pending'
    case 'restart_failed':
      return 'error'
    case 'unknown':
      return 'warning'
    default:
      return 'info'
  }
}

function ServiceCard({
  name,
  health,
  icon: Icon,
}: {
  name: string
  health?: {
    status: string
    latency_ms?: number
    error?: string
    version?: string
    message?: string
  }
  icon: React.ElementType
}) {
  const status = health?.status || 'unknown'
  const isHealthy = status === 'healthy'
  const isDisabled = status === 'disabled'

  return (
    <div className="bubble p-4 hover-lift cursor-default">
      <div className="flex items-center gap-3 mb-2">
        <div
          className={cn(
            'w-9 h-9 rounded-lg flex items-center justify-center',
            isHealthy
              ? 'bg-emerald-500/10 text-emerald-400'
              : isDisabled
                ? 'bg-gray-500/10 text-gray-400'
                : 'bg-red-500/10 text-red-400'
          )}
        >
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{name}</p>
          <p
            className={cn(
              'text-xs',
              isHealthy ? 'text-emerald-400' : isDisabled ? 'text-gray-400' : 'text-red-400'
            )}
          >
            {isHealthy ? 'Healthy' : isDisabled ? 'Disabled' : 'Unhealthy'}
          </p>
        </div>
      </div>
      {health?.latency_ms !== undefined && (
        <p className="text-xs text-muted-foreground">{health.latency_ms.toFixed(1)} ms</p>
      )}
      {health?.version && <p className="text-xs text-muted-foreground">v{health.version}</p>}
      {health?.error && (
        <Tooltip content={health.error}>
          <p className="text-xs text-red-400/80 truncate cursor-help">{health.error}</p>
        </Tooltip>
      )}
      {health?.message && <p className="text-xs text-muted-foreground">{health.message}</p>}
    </div>
  )
}

function ResourceCard({
  label,
  percent,
  icon: Icon,
  details,
}: {
  label: string
  percent: number
  icon: React.ElementType
  details: { label: string; value: string }[]
}) {
  const percentage = Math.min(Math.max(percent, 0), 100)
  const colorClass =
    percentage >= 90 ? 'text-red-400' : percentage >= 70 ? 'text-amber-400' : 'text-emerald-400'
  const bgClass =
    percentage >= 90 ? 'bg-red-500/10' : percentage >= 70 ? 'bg-amber-500/10' : 'bg-emerald-500/10'
  const barColor =
    percentage >= 90 ? 'bg-red-400' : percentage >= 70 ? 'bg-amber-400' : 'bg-emerald-400'

  return (
    <div className="bubble p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={cn('p-1.5 rounded-md', bgClass)}>
            <Icon className={cn('w-4 h-4', colorClass)} />
          </div>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className={cn('text-lg font-bold tabular-nums', colorClass)}>
          {percentage.toFixed(1)}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden mb-3">
        <motion.div
          className={cn('h-full rounded-full', barColor)}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </div>
      <div className="text-xs text-muted-foreground space-y-0.5">
        {details.map((d) => (
          <div key={d.label} className="flex justify-between">
            <span>{d.label}</span>
            <span className="font-mono text-foreground">{d.value}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function DiskCard({
  label,
  disk,
  icon: Icon,
}: {
  label: string
  disk: {
    path: string
    percent: number
    total_bytes: number
    used_bytes: number
    free_bytes: number
    fstype: string | null
  }
  icon: React.ElementType
}) {
  const percentage = Math.min(Math.max(disk.percent, 0), 100)
  const colorClass =
    percentage >= 90 ? 'text-red-400' : percentage >= 70 ? 'text-amber-400' : 'text-emerald-400'
  const bgClass =
    percentage >= 90 ? 'bg-red-500/10' : percentage >= 70 ? 'bg-amber-500/10' : 'bg-emerald-500/10'
  const barColor =
    percentage >= 90 ? 'bg-red-400' : percentage >= 70 ? 'bg-amber-400' : 'bg-emerald-400'

  return (
    <div className="bubble p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={cn('p-1.5 rounded-md', bgClass)}>
            <Icon className={cn('w-4 h-4', colorClass)} />
          </div>
          <span className="text-sm font-medium">{label}</span>
        </div>
        <span className={cn('text-lg font-bold tabular-nums', colorClass)}>
          {percentage.toFixed(1)}%
        </span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden mb-3">
        <motion.div
          className={cn('h-full rounded-full', barColor)}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </div>
      <div className="text-xs text-muted-foreground space-y-0.5">
        <div className="flex justify-between">
          <span>Path</span>
          <span className="font-mono text-foreground">{disk.path}</span>
        </div>
        <div className="flex justify-between">
          <span>Type</span>
          <span className="font-mono text-foreground">{disk.fstype || '—'}</span>
        </div>
        <div className="flex justify-between">
          <span>Total</span>
          <span className="font-mono text-foreground">{formatBytes(disk.total_bytes)}</span>
        </div>
        <div className="flex justify-between">
          <span>Used</span>
          <span className="font-mono text-foreground">{formatBytes(disk.used_bytes)}</span>
        </div>
        <div className="flex justify-between">
          <span>Free</span>
          <span className="font-mono text-foreground">{formatBytes(disk.free_bytes)}</span>
        </div>
      </div>
    </div>
  )
}
